/**
 * FullCalendar day-column header for self-schedule.
 *
 * Consumes the `DayCheckbox` enum directly — the four states
 * (`empty | partial | full | disabled`) encode every UI case
 * without the component needing to reason about
 * `isChecked + isIndeterminate + isDisabled` independently. See
 * `state/selectors.ts` for how the enum is derived.
 *
 * Tooltip wording for the disabled state is decided by the caller
 * (SelfScheduleCalendar) and handed in via `disabledReason`.
 *
 * Tooltip is scoped to the checkbox only — hovering the date label
 * does NOT fire the tooltip. Disabled styling (`opacity-50` +
 * `cursor-not-allowed` from `checkboxVariants`) is preserved because
 * we wrap the checkbox in a span that captures hover on behalf of
 * the disabled input.
 */
import { Box, Flex } from '@chakra-ui/react';
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import moment from 'moment-timezone';
import { KText } from '@/ui-library/components/text';
import type { DayCheckbox } from '../state/selectors';

interface DayCheckboxHeaderProps {
  date: Date;
  /** Enum drives checked + indeterminate + disabled in one value. */
  state: DayCheckbox;
  /** Shown in the tooltip when `state === 'disabled'`. */
  disabledReason?: string;
  /** `true` means "please flip this day on". Reducer decides what
   *  "on" means for empty vs. partial vs. full transitions. */
  onToggle: (_checked: boolean) => void;
}

export function DayCheckboxHeader({
  date,
  state,
  disabledReason,
  onToggle,
}: DayCheckboxHeaderProps) {
  const m = moment(date);
  const isToday = m.isSame(moment(), 'day');

  const isChecked = state === 'full';
  const isIndeterminate = state === 'partial';
  const isDisabled = state === 'disabled';

  const checkboxEl = (
    <Checkbox
      size="sm"
      checked={isChecked}
      indeterminate={isIndeterminate}
      disabled={isDisabled}
      // Click rules:
      //   empty     → onToggle(true)   → reducer sets autoFill
      //   partial   → onToggle(true)   → commit to full
      //   full      → onToggle(false)  → reducer drops auto-fill
      // The Kula Checkbox `e.target.checked` flips to the inverse
      // of the current `checked` prop, but for `partial` state it
      // toggles to `true` — so we coerce via the enum.
      onChange={() => {
        if (isDisabled) return;
        if (state === 'full') onToggle(false);
        else onToggle(true);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );

  // Always wrap in the same `<Box as="span">` so checked and
  // disabled checkboxes share identical layout context — without
  // this, the disabled variant sits a pixel higher because only
  // its tooltip-wrapping span was enforcing `inline-flex`.
  const wrapped = (
    <Box as="span" display="inline-flex" lineHeight={0} mt="3px">
      {checkboxEl}
    </Box>
  );
  const checkboxWithTooltip =
    isDisabled && disabledReason ? (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>{wrapped}</TooltipTrigger>
          <TooltipContent side="top">{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      wrapped
    );

  return (
    <Flex
      direction="row"
      align="flex-start"
      justify="space-between"
      p="5px"
      color={isToday ? 'primary-500' : 'secondary-700'}
      w="100%"
      h="100%"
    >
      <Flex direction="column" align="flex-start" lineHeight="1.1">
        <KText fontSize="11px" fontWeight="500" textTransform="uppercase">
          {m.format('ddd')}
        </KText>
        <KText fontSize="18px" fontWeight="600">
          {m.format('D')}
        </KText>
      </Flex>
      {checkboxWithTooltip}
    </Flex>
  );
}
